import { type FC, useCallback, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type CapacityCardStatus = "default" | "success" | "warning" | "error";

export interface CapacityCardProps {
    /** Uppercase eyebrow label (e.g. "ROVO CREDITS") */
    label: string;
    /** Current/estimated value */
    value: number;
    /** Plan limit — when provided, shows "value / maxValue" format */
    maxValue?: number;
    /** Descriptive text below the value */
    description?: string;
    /** Visual status state */
    status?: CapacityCardStatus;
    /** Whether the card can be collapsed/expanded */
    isCollapsible?: boolean;
    /** Initial expanded state (default: true) */
    defaultExpanded?: boolean;
    /** Additional CSS class name */
    className?: string;
}

const cardStyles = css({
    backgroundColor: wacToken("color.neutral.light0"),
    border: `1px solid ${wacToken("color.neutral.light300")}`,
    borderRadius: "12px",
    padding: `${token("space.200")} ${token("space.250")}`,
    width: "100%",
    boxSizing: "border-box"
});

const headerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const labelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 600,
    fontSize: "11px",
    lineHeight: "16px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: wacToken("color.neutral.light700")
});

const valueContainerStyles = css({
    marginBlockStart: token("space.050")
});

const valueStatusStyles = {
    default: css({
        color: wacToken("color.primary.black")
    }),
    success: css({
        color: wacToken("color.primary.black")
    }),
    warning: css({
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#974F0C"
    }),
    error: css({
        color: wacToken("color.secondary.red")
    })
};

const valueStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "28px",
    lineHeight: "36px"
});

const maxValueStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "28px",
    lineHeight: "36px",
    color: wacToken("color.neutral.light700")
});

const descriptionStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.neutral.light700"),
    marginBlockStart: token("space.100"),
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
});

const toggleButtonStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "width": "24px",
    "height": "24px",
    "background": "none",
    "border": "none",
    "cursor": "pointer",
    "padding": 0,
    "borderRadius": "4px",
    "color": wacToken("color.neutral.light700"),
    "flexShrink": 0,

    "&:hover": {
        backgroundColor: wacToken("color.neutral.light200")
    },

    "&:focus-visible": {
        boxShadow: `0 0 0 2px ${wacToken(
            "color.neutral.light0"
        )}, 0 0 0 4px ${wacToken("color.primary.blue")}`
    }
});

const chevronStyles = css({
    width: "16px",
    height: "16px",
    transition: "transform 0.2s ease"
});

const chevronCollapsedStyles = css({
    transform: "rotate(0deg)"
});

const chevronExpandedStyles = css({
    transform: "rotate(180deg)"
});

const formatNumber = (num: number): string => num.toLocaleString("en-US");

const ChevronIcon: FC<{ expanded: boolean }> = ({ expanded }) => (
    <svg
        css={[
            chevronStyles,
            expanded ? chevronExpandedStyles : chevronCollapsedStyles
        ]}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const CapacityCard: FC<CapacityCardProps> = ({
    label,
    value,
    maxValue,
    description,
    status = "default",
    isCollapsible = true,
    defaultExpanded = true,
    className
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const handleToggle = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const showComparison = maxValue !== undefined && maxValue !== null;
    const isOverCapacity = showComparison && value > (maxValue as number);
    const effectiveStatus = isOverCapacity
        ? status === "default"
            ? "error"
            : status
        : status;

    const descriptionId = `capacity-card-desc-${label
        .replace(/\s+/g, "-")
        .toLowerCase()}`;

    return (
        <div
            css={cardStyles}
            className={className}
            data-testid={`capacity-card-${label
                .replace(/\s+/g, "-")
                .toLowerCase()}`}
        >
            <div css={headerStyles}>
                <span css={labelStyles}>{label}</span>
                {isCollapsible && (
                    <button
                        css={toggleButtonStyles}
                        onClick={handleToggle}
                        aria-expanded={isExpanded}
                        aria-controls={
                            isExpanded && description
                                ? descriptionId
                                : undefined
                        }
                        aria-label={`${
                            isExpanded ? "Collapse" : "Expand"
                        } ${label} details`}
                    >
                        <ChevronIcon expanded={isExpanded} />
                    </button>
                )}
            </div>

            <div css={valueContainerStyles}>
                <span css={[valueStyles, valueStatusStyles[effectiveStatus]]}>
                    {formatNumber(value)}
                </span>
                {showComparison && (
                    <span css={maxValueStyles}>
                        {" "}
                        / {formatNumber(maxValue as number)}
                    </span>
                )}
            </div>

            {isExpanded && description && (
                <p css={descriptionStyles} id={descriptionId}>
                    {description}
                </p>
            )}
        </div>
    );
};
